// Player-related utility functions

export const getPositionDisplay = (player, slotPosition, playersByPosition) => {
  // First find which position array the player belongs to
  for (const pos in playersByPosition) {
    if (playersByPosition[pos].some(p => p.name === player.name)) {
      return pos;
    }
  }
  return '';
};

export const getPlayerTeam = (playerName, playersByPosition) => {
  for (const pos in playersByPosition) {
    const player = playersByPosition[pos].find(p => p.name === playerName);
    if (player) {
      return player.team;
    }
  }
  return '';
};

export const getPlayerProjected = (playerName, playersByPosition) => {
  for (const pos in playersByPosition) {
    const player = playersByPosition[pos].find(p => p.name === playerName);
    if (player) {
      return player.projected.toFixed(1);
    }
  }
  return '0.0';
};

// Color for an actual (final) score displayed next to its projection: green
// when the score beat the projection, red otherwise. The further the score is
// from the projection, the darker the shade — scaled by percent difference and
// maxing out at 40% off, so player and team totals shade comparably.
export const getFinalPointsColor = (finalPoints, projected) => {
  const distance = projected > 0
    ? Math.min(Math.abs(finalPoints - projected) / projected, 0.4) / 0.4
    : 1;
  const lightness = 70 - distance * 35;
  return `hsl(${finalPoints > projected ? 120 : 0}, 70%, ${lightness}%)`;
};

export const getFormattedTeamAndPosition = (playerName, slotPosition, playersByPosition) => {
  const team = getPlayerTeam(playerName, playersByPosition);
  const position = getPositionDisplay({ name: playerName }, slotPosition, playersByPosition);

  // For BENCH, FLEX, and SFLEX, show both team and position
  if (['BENCH', 'FLEX', 'SFLEX'].includes(slotPosition)) {
    return `(${team} - ${position})`;
  }

  // For other positions, just show team
  return `(${team})`;
};

export const getAvailablePlayers = (position, availablePlayers, searchTerm = '') => {
  let players;
  switch (position) {
    case 'BENCH':
      players = [
        ...availablePlayers.QB,
        ...availablePlayers.RB,
        ...availablePlayers.WR,
        ...availablePlayers.TE,
        ...availablePlayers.K,
        ...availablePlayers.DEF
      ];
      break;
    case 'FLEX':
      players = [
        ...availablePlayers.RB,
        ...availablePlayers.WR,
        ...availablePlayers.TE
      ];
      break;
    case 'SFLEX':
      players = [
        ...availablePlayers.QB,
        ...availablePlayers.RB,
        ...availablePlayers.WR,
        ...availablePlayers.TE
      ];
      break;
    default:
      players = availablePlayers[position] || [];
  }
  return players.filter(player =>
    player.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
};

export const getBestAvailableForPosition = (pos, playersByPosition, selectedPlayers) => {
  let availablePlayers;
  switch (pos) {
    case 'BENCH':
      availablePlayers = [
        ...playersByPosition.QB,
        ...playersByPosition.RB,
        ...playersByPosition.WR,
        ...playersByPosition.TE,
        ...playersByPosition.K,
        ...playersByPosition.DEF
      ];
      break;
    case 'FLEX':
      availablePlayers = [
        ...playersByPosition.RB,
        ...playersByPosition.WR,
        ...playersByPosition.TE
      ];
      break;
    case 'SFLEX':
      availablePlayers = [
        ...playersByPosition.QB,
        ...playersByPosition.RB,
        ...playersByPosition.WR,
        ...playersByPosition.TE
      ];
      break;
    default:
      availablePlayers = playersByPosition[pos];
  }

  return availablePlayers
    .filter(p => !selectedPlayers.has(p.name))
    .sort((a, b) => b.projected - a.projected)[0];
};

export const getAllPlayers = (customPlayerData, availablePlayers) => {
  const allPlayers = [];
  const sourceData = customPlayerData || availablePlayers;

  Object.entries(sourceData).forEach(([position, players]) => {
    players.forEach(player => {
      allPlayers.push({
        ...player,
        position
      });
    });
  });
  return allPlayers;
};

export const sortPlayers = (players, sorting, playersByPosition) => {
  return [...players].sort((a, b) => {
    let comparison = 0;
    switch (sorting.field) {
      case 'name':
        const aLastName = a.name.split(' ').slice(-1)[0];
        const bLastName = b.name.split(' ').slice(-1)[0];
        comparison = aLastName.localeCompare(bLastName);
        break;
      case 'team':
        comparison = a.team.localeCompare(b.team);
        break;
      case 'projected':
        comparison = a.projected - b.projected;
        break;
      case 'position':
        const aPos = getPositionDisplay(a, '', playersByPosition);
        const bPos = getPositionDisplay(b, '', playersByPosition);
        comparison = aPos.localeCompare(bPos);
        break;
      default:
        comparison = 0;
    }
    return sorting.direction === 'asc' ? comparison : -comparison;
  });
};
